import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { Portal } from '@headlessui/react';

import ChevronDown from '@/icons/light/chevron-down.svg';
import Facebook from '@/icons/light/facebook.svg';
import Mail from '@/icons/light/mail.svg';
import Twitter from '@/icons/light/twitter.svg';
import { Button, IconText, Link, Price, Text } from '@/atoms';
import { ButtonSize, ButtonVariant } from '@/atoms/button/types';
import { IconTextVariant } from '@/atoms/icon-text/types';
import {
  Alert,
  ModalShare,
  ModalVideo,
  ModalView360,
  Video,
  View360,
} from '@/molecules';

import type { ComponentType, FunctionComponent } from 'react';
import type { TypeFlatDescriptionProps } from './types';

const ModalForm = dynamic(() => import('@/molecules/modal-form'), {
  ssr: true,
});

const FormsTabs = dynamic(() => import('@/organisms/forms-tabs'), {
  ssr: true,
});

const AvailableDates = dynamic(() => import('@/organisms/available-dates'), {
  ssr: true,
});

export const FlatDescription: FunctionComponent<TypeFlatDescriptionProps> = ({
  className = '',
  price,
  title,
  description,
  has360,
  src360,
  hasVideo,
  srcVideo,
  highlightedFeatures,
  features,
  location,
  flat,
  isPeriodic = true,
  availableDates,
  otrosDatos,
  registroPropiedad = '',
  viewStyle = 'legacy',
  beforeFeatures,
}: TypeFlatDescriptionProps) => {
  const isHero = viewStyle === 'hero';
  const [url, setUrl] = useState('');
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [open, setOpen] = useState<boolean>(false);
  const Map = location?.location
    ? dynamic(() => import('@/molecules/map'), {
        ssr: false,
      })
    : null;
  const showLimitedAvailabilityAlert: boolean =
    availableDates &&
    availableDates.length > 1 &&
    flat.ventaAlquiler === 'MED_EST';
  const showAvailabilityCalendar: boolean =
    availableDates &&
    availableDates.length !== 0 &&
    flat.ventaAlquiler === 'MED_EST';

  useEffect(() => {
    setUrl(document.URL);
    return () => setUrl('');
  }, []);

  return (
    <>
      <div className={`flex flex-col ${className}`}>
        {!isHero && (
          <h1 className="text-headline text-headline-h3 mb-4">{title}</h1>
        )}

        <div
          className={`flex flex-col justify-between gap-4 md:flex-row md:flex-wrap ${
            isHero ? 'mb-12 md:gap-x-6 md:gap-y-12' : 'mb-8 md:gap-6'
          }`}
        >
          <Price
            period={isPeriodic ? '/mes' : ''}
            price={price}
            mark={isHero}
            className={
              isHero
                ? 'order-1 [&_mark]:font-["Manrope"]! [&_mark]:text-[64px]! [&_mark]:leading-[72px]! [&_mark]:font-bold! max-md:[&_mark]:text-3xl! max-md:[&_mark]:leading-10!'
                : 'order-1'
            }
          />

          <div
            className={`order-2 flex flex-row items-center gap-x-4 md:gap-x-6 ${
              isHero
                ? '[&_button]:text-text-primary! [&_a]:text-text-primary!'
                : ''
            }`}
          >
            {hasVideo && srcVideo && (
              <ModalVideo type="link" src={srcVideo} flat={flat} />
            )}

            {has360 && src360 && (
              <ModalView360 type="link" src={src360} flat={flat} />
            )}

            <ModalShare
              className="order-3 md:order-2"
              title="Comparte esta vivienda"
              socialMedias={[
                {
                  Icon: <Mail width={32} heigth={32} />,
                  text: 'Email',
                  href: 'mailto:?subject=Feedback&body=Message',
                },
                {
                  Icon: <Twitter width={32} heigth={32} />,
                  text: 'Twitter',
                  href: `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
                },
                {
                  Icon: <Facebook width={32} heigth={32} />,
                  text: 'Facebook',
                  href: `https://www.facebook.com/sharer.php?u=${url}`,
                },
              ]}
              gtm={{ itemId: flat.codigoUnidadAlquiler }}
            />
          </div>

          {highlightedFeatures && (
            // En móvil el rediseño hero deja un respiro extra entre la fila
            // de Compartir/Gallery/Video y la rejilla de features (Figma);
            // el render legacy mantiene su espaciado histórico sin mt-8.
            <div
              className={`order-2 w-full md:order-3 ${isHero ? 'mt-8 md:mt-0' : ''}`}
            >
              {isHero ? (
                <div className="grid grid-cols-3 gap-5 md:grid-cols-6 md:gap-4">
                  {highlightedFeatures.map((feature, index: number) => (
                    <div
                      key={index}
                      // Proporciones Figma en móvil: icono 32px alto (frame
                      // 48×32) + texto p_L + padding que deja el ring en ~100:92.
                      // La altura la marca el contenido (no se fija), así el texto
                      // nunca se recorta aunque iOS lo renderice algo más grande.
                      // `w-full` hace que el ring ocupe la celda del grid (~100px)
                      // en vez de un `w-32` fijo que desbordaba y pegaba los rings.
                      // En md+ recupera el icono/ancho/padding originales.
                      className="feature-highlight-ring mx-auto flex w-full flex-col items-center justify-center gap-2 px-2 pt-[10px] pb-[16px] text-center md:w-32 md:pb-[22px]"
                    >
                      <div className="flex h-8 w-12 items-center justify-center md:size-12">
                        <feature.Icon className="text-text-primary size-8 md:size-10" />
                      </div>
                      <span className="text-style-xl font-secondary text-text-primary">
                        {feature.value}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="xs:grid-cols-3 grid grid-cols-2 gap-4 md:w-max md:grid-cols-5 xl:grid-cols-6">
                  {highlightedFeatures.map((feature, index: number) => (
                    <IconText
                      Icon={feature.Icon}
                      value={feature.value}
                      variant={IconTextVariant.MEDIUM}
                      key={index}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div
          className={`flex flex-col gap-y-4 ${
            !isHero || registroPropiedad ? 'mb-8' : ''
          }`}
        >
          <div className="flex flex-col gap-y-2">
            {registroPropiedad && (
              <Text className="text-body text-body-l text-text-neutrals-900">
                NÚMERO DE REGISTRO ARRENDAMIENTO “NRA”:
                <span className="text-body-m text-text-neutrals-500">
                  {' '}
                  {registroPropiedad}
                </span>
              </Text>
            )}
          </div>
        </div>

        {showLimitedAvailabilityAlert && (
          <Alert link="#available-dates" className="mb-8" />
        )}

        {description && (
          <>
            <Text
              className={`text-headline text-headline-h4 mb-4 ${
                isHero ? 'text-primary-500 font-bold' : ''
              }`}
              as="h2"
            >
              {description.title}
            </Text>

            <div
              className={`mb-8 ${
                isHero
                  ? 'text-body-s font-secondary text-[var(--inputbg)]'
                  : 'text-body text-body-m'
              }`}
              // El contenido viene de SAP/Algolia y muchas entradas terminan
              // con cadenas tipo `<br><br><br><br>` que dejan un hueco feo
              // bajo el bloque. Stripeamos `<br>` (con o sin /), espacios y
              // &nbsp; del final, manteniendo intactos los `<br>` interiores.
              dangerouslySetInnerHTML={{
                __html: description.content.replace(
                  /(?:\s|&nbsp;|<br\s*\/?>)+$/i,
                  '',
                ),
              }}
            />
          </>
        )}

        {otrosDatos && (
          <div className="mb-6 flex flex-col">
            <p className="text-headline text-headline-h4 text-text-neutrals-900">
              Otros datos de interés
            </p>

            <div
              className="text-body text-body-m text-text-neutrals-900"
              dangerouslySetInnerHTML={{ __html: otrosDatos }}
            />
          </div>
        )}

        {hasVideo && srcVideo && (
          <Video className="mb-12" src={srcVideo} flat={flat} />
        )}

        {has360 && src360 && (
          <View360 className="mb-12" src={src360} flat={flat} />
        )}

        {beforeFeatures && (
          // Si el bloque hijo (típicamente CTAHipoteca) tiene HideOnMobile,
          // ocultamos también este wrapper para que su `mb-8` no deje un
          // hueco visible en móvil.
          <div
            className={`mb-8 ${
              (beforeFeatures as any)?.props?.HideOnMobile
                ? 'hidden md:block'
                : ''
            }`}
          >
            {beforeFeatures}
          </div>
        )}

        {features && features.features.length > 1 && (
          <div className="mb-6 flex flex-col gap-y-4">
            <Text
              className={`text-headline text-headline-h4 ${
                isHero ? 'text-primary-500 font-bold' : ''
              }`}
              as="h2"
            >
              {features.title}
            </Text>

            {features.features &&
              (isHero ? (
                <div className="flex flex-wrap gap-5">
                  {features.features.map((f, i) => {
                    const Icon = dynamic(
                      () => import(`@/icons/light/${f.icon}.svg`),
                    ) as ComponentType<{ className?: string }>;
                    return (
                      <div
                        key={i}
                        className={`bg-primary-500/50 inline-flex items-center gap-4 rounded-full px-4 py-2 text-[var(--inputbg)] ${
                          i > 5 && !open ? 'hidden' : ''
                        }`}
                      >
                        <Icon className="size-8 shrink-0" />
                        <span className="md:text-style-xl font-secondary text-base">
                          {f.renderValue && f.value
                            ? `${f.title} ${f.value}`
                            : f.title}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="xs:grid-cols-2 grid grid-cols-1 gap-y-4 md:grid-cols-3">
                  {features.features.map((f, i) => (
                    <IconText
                      Icon={dynamic(
                        () => import(`@/icons/light/${f.icon}.svg`),
                      )}
                      variant={IconTextVariant.MEDIUM}
                      key={i}
                      title={f.title}
                      value={f.renderValue ? f.value : ''}
                      {...(i > 5 &&
                        !open && {
                          className: 'hidden',
                        })}
                    />
                  ))}
                </div>
              ))}

            {features.features.length > 6 && (
              <Link as="button" onClick={() => setOpen(!open)}>
                {open ? 'Ver menos' : 'Ver todas'}{' '}
                <ChevronDown
                  width={16}
                  height={16}
                  {...(open && {
                    className: 'rotate-180',
                  })}
                />
              </Link>
            )}
          </div>
        )}

        {location && (
          <div
            className={`isolate flex flex-col gap-y-4 ${
              isHero
                ? 'bg-neutrals-0 p-5 drop-shadow-[0px_8px_8px_rgba(0,0,0,0.16)]'
                : ''
            }`}
          >
            <Text
              className={`text-headline text-headline-h4 ${
                isHero ? 'text-primary-500 font-bold' : ''
              }`}
              as="h2"
            >
              {location.title}
            </Text>

            {location?.address}

            {Map ? (
              <Map
                location={location.location}
                minHeight={isHero ? 360 : 240}
              />
            ) : (
              Map
            )}
          </div>
        )}

        {showAvailabilityCalendar && (
          <AvailableDates availableDates={availableDates} />
        )}
      </div>

      <ModalForm isOpen={isOpenModal} closeModal={() => setIsOpenModal(false)}>
        <FormsTabs flat={flat} noTitle />
      </ModalForm>

      <Portal>
        {/* `after:*` sangra el fondo de la barra unos px por debajo de
            `bottom-0`. En iOS Safari la barra fija queda anclada justo encima
            de la toolbar inferior y un redondeo sub-pixel deja asomar el
            contenido que scrollea por detrás; este relleno tapa ese hueco. En
            el resto de navegadores queda fuera de pantalla, así que no cambia
            nada. */}
        <div className="border-t-solid border-t-neutrals-50 bg-neutrals-0 after:bg-neutrals-0 fixed right-0 bottom-0 left-0 z-10 flex h-16 [transform:translateZ(0)] items-center justify-between gap-4 border-t px-4 py-2 [backface-visibility:hidden] after:absolute after:inset-x-0 after:top-full after:h-6 after:content-[''] lg:hidden">
          <Price period={isPeriodic ? '/mes' : ''} price={price} />

          <Button
            onClick={() => setIsOpenModal(true)}
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.SMALL}
            className="w-max"
          >
            Contactar
          </Button>
        </div>
      </Portal>
    </>
  );
};

FlatDescription.displayName = 'FlatDescription';
